import type { RouteObject } from 'react-router-dom'

import { MembraneClient, MembraneClientProvider } from '@membranehq/react'
import { createReactRouterAdapter, RouterAdapterProvider } from '@membranehq/router-adapter'
import { PropsWithChildren, Suspense, useEffect, useMemo } from 'react'
import { createBrowserRouter, Outlet, RouterProvider, useMatches, useRouteError } from 'react-router-dom'
import { Button } from 'ui-kit/button'

import { RefreshConnectionPageLazy } from '../components/Connections/RefreshConnectionPopup.lazy'
import { EmbedIsolateWindow } from '../components/EmbedIsolate/EmbedIsolateWindow'
import { EmbedPopupWindow } from '../components/EmbedPopup/EmbedPopupWindow'
import { PopupSizeProvider } from '../components/EmbedPopup/popup-size-context'
import { ErrorPopupProvider } from '../components/Error/ErrorPopup'
import { EmptyPageLoader } from '../components/Loader'
import { BreadcrumbsProvider } from '../components/Navigation/breadcrumbs-context'
import { Popup } from '../components/Popup'
import { TooltipProvider } from '../components/Tooltip'
import { ENV_CONFIG } from '../config/env'
import { AuthProvider } from '../contexts/auth'
import { useAuth } from '../contexts/use-auth'
import { DataSourceInstancePageLazy } from '../routes/Embed/DataSourceInstances.lazy'
import { FieldMappingInstancePageLazy } from '../routes/Embed/FieldMappingInstances.lazy'
import { FlowInstancesPageLazy } from '../routes/Embed/FlowInstances.lazy'
import { FlowRunPageLazy } from '../routes/Embed/FlowRuns.lazy'
import { IntegrationRoutesLazy } from '../routes/Embed/Integrations/Integration.lazy'
import { IntegrationAppTopPageLazy } from '../routes/Embed/Main.lazy'
import { IsolatedFlowInstancesContainerLazy } from '../routes/IsolatedEmbed/FlowInstances.lazy'
import { IsolatedFlowRunPageLazy } from '../routes/IsolatedEmbed/FlowRuns.lazy'

function SuspensedOutlet() {
  const matches = useMatches()
  const matchId = matches.at(-1)?.id
  return (
    <Suspense fallback={<EmptyPageLoader />} key={matchId}>
      <Outlet />
    </Suspense>
  )
}

const reactRouterAdapter = createReactRouterAdapter()

function AppContextsWrapper({ children }: PropsWithChildren) {
  return (
    <RouterAdapterProvider value={reactRouterAdapter}>
      <ErrorPopupProvider>
        <AuthProvider>
          <ClientProvider>{children}</ClientProvider>
        </AuthProvider>
      </ErrorPopupProvider>
    </RouterAdapterProvider>
  )
}

function EmbedPopupLayout() {
  return (
    <PopupSizeProvider>
      <BreadcrumbsProvider>
        <EmbedPopupWindow>
          <SuspensedOutlet />
        </EmbedPopupWindow>
      </BreadcrumbsProvider>
    </PopupSizeProvider>
  )
}

function EmbedIsolateLayout() {
  return (
    <BreadcrumbsProvider>
      <EmbedIsolateWindow>
        <SuspensedOutlet />
      </EmbedIsolateWindow>
    </BreadcrumbsProvider>
  )
}

const routeConfig: RouteObject[] = [
  {
    element: (
      <AppContextsWrapper>
        <SuspensedOutlet />
      </AppContextsWrapper>
    ),
    errorElement: <EmbedErrorPage />,
    children: [
      {
        element: <EmbedPopupLayout />,
        errorElement: <EmbedErrorPage />,
        children: [
          {
            path: '/embed/connections/:connectionId/refresh',
            element: <RefreshConnectionPageLazy />,
          },
          {
            path: '/embed/data-source-instances/:dataSourceInstanceId/configuration',
            element: <DataSourceInstancePageLazy />,
          },
          {
            path: '/embed/field-mapping-instances/:fieldMappingInstanceId/configuration',
            element: <FieldMappingInstancePageLazy />,
          },
          {
            path: '/embed/flow-instances/*',
            element: <FlowInstancesPageLazy />,
          },
          {
            path: '/embed/flow-runs/*',
            element: <FlowRunPageLazy />,
          },
          {
            path: '/embed/integrations/:integrationId/*',
            element: <IntegrationRoutesLazy standalone={true} basePath='/embed/integrations' />,
          },
          {
            path: '/embed/main/*',
            element: <IntegrationAppTopPageLazy />,
          },
          {
            path: '*',
            element: <NotFoundPage />,
          },
        ],
      },
      {
        element: <EmbedIsolateLayout />,
        errorElement: <EmbedErrorPage />,
        children: [
          {
            path: '/isolated-embed/flow-instances/:flowInstanceId/editor/*',
            element: <IsolatedFlowInstancesContainerLazy />,
          },
          {
            path: '/isolated-embed/flow-runs/:flowRunId/editor/*',
            element: <IsolatedFlowRunPageLazy />,
          },
        ],
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]

const router = createBrowserRouter(routeConfig)

export function RootRoutes() {
  return (
    <TooltipProvider>
      <RouterProvider router={router} />
    </TooltipProvider>
  )
}

function NotFoundPage() {
  return (
    <Popup.Content className='flex flex-col gap-0 justify-center items-center'>
      <h1 className='text-2xl font-semibold p-0'>Not found</h1>
      <p className='text-lg p-0'>The page you are looking for does not exist.</p>
    </Popup.Content>
  )
}

/**
 * Renders in place of a screen that failed, so one broken screen does not blank the embed.
 * React Router shows its own developer error screen when a route has no `errorElement`.
 * The embedded UI runs inside a customer product, so it shows a short recovery message
 * and logs the technical detail to the console instead.
 *
 * React Router mounts this in place of the failing route, which drops every provider that
 * route rendered. So the markup here uses a plain element and its own classes rather than
 * `Popup.Content`, and depends on no context at all — a fallback that can throw is no
 * fallback.
 */
function EmbedErrorPage() {
  const error = useRouteError()

  useEffect(() => {
    console.error('[membrane-ui] embedded screen failed to render', error)
  }, [error])

  return (
    <div className='flex flex-col gap-2 justify-center items-center h-full p-6 overflow-y-auto'>
      <h1 className='text-2xl font-semibold p-0'>Something went wrong</h1>
      <p className='text-lg p-0'>This screen could not be displayed. Please try again.</p>
      <Button onClick={() => window.location.reload()}>Reload</Button>
    </div>
  )
}

function ClientProvider({ children }: PropsWithChildren) {
  const { token } = useAuth()

  const client = useMemo(() => {
    if (!token) {
      return null
    }

    return new MembraneClient({
      token,
      apiUri: ENV_CONFIG.ENGINE_URI,
    })
  }, [token])

  return <MembraneClientProvider client={client}>{children}</MembraneClientProvider>
}
